import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import {
  useQueryDrugFactsPanel,
  useQueryFactsPanel,
  useQueryPetFoodPanel,
  useQuerySupplementFactsPanel,
} from '../facts-panel/useQueryFactsPanel';

import * as productServices from 'services/product';
import { CustomNotification } from 'common/components';

import {
  groupProductData,
  useInvalidateFactsPanel,
  useInvalidateGroupDetail,
} from '../../shared/hooks';

import { forwardTo } from 'utils/common/route';

export const useFactsPanelsGroup = (productId) => {
  const { listFactsPanel = [] } = useQueryFactsPanel({
    id: productId,
  });

  const { listSupplementFactsPanel = [] } = useQuerySupplementFactsPanel({
    id: productId,
  });

  const { listDrugFactsPanel = [] } = useQueryDrugFactsPanel({
    id: productId,
  });

  const { listPetFoodPanel = [] } = useQueryPetFoodPanel({
    id: productId,
  });

  return {
    factsPanelsGroup: [
      ...listFactsPanel,
      ...listPetFoodPanel,
      ...listDrugFactsPanel,
      ...listSupplementFactsPanel,
    ],
  };
};

export const useSaveGroupProduct = (productId, groupId) => {
  const queryClient = useQueryClient();

  const { handleSyncAllTabPanels } = useInvalidateFactsPanel(productId);
  const { handleSyncSingleDetailGroup } = useInvalidateGroupDetail(
    productId,
    groupId
  );

  return useMutation({
    mutationFn: productServices.saveGroupProductData,
    onSuccess: async (result) => {
      if (result?.isSuccess) {
        queryClient.invalidateQueries({
          queryKey: groupProductData.getAllGroups(productId),
        });
        handleSyncAllTabPanels();
        handleSyncSingleDetailGroup();
      } else {
        CustomNotification.error(result?.message ?? 'Something went wrong!');
      }
    },
    onError: (error) => {
      CustomNotification.error(error?.message ?? 'Something went wrong!');
    },
  });
};

export const useGetGroupDetail = ({ productId, groupId }) => {
  const queries = useQuery({
    queryKey: groupProductData.getDetailGroup(productId, groupId),
    queryFn: async () => {
      const response = await productServices.getGroupDataDetail({
        productItemId: productId,
        groupId,
      });
      if (response?.isSuccess) {
        return response?.data;
      } else {
        CustomNotification.error(response?.message ?? 'Something went wrong!');
        forwardTo('/products');
      }
    },
    enabled: !!productId && !!groupId,
  });

  return {
    groupDetail: queries?.data ?? null,
    ...queries,
  };
};

export const useAssignGroupProduct = (productId, groupId) => {
  const queryClient = useQueryClient();

  const { handleSyncAllTabPanels } = useInvalidateFactsPanel(productId);
  const { handleSyncSingleDetailGroup } = useInvalidateGroupDetail(
    productId,
    groupId
  );

  return useMutation({
    mutationFn: productServices.assignGroupProductData,
    onSuccess: async (result) => {
      if (result?.isSuccess) {
        queryClient.invalidateQueries({
          queryKey: groupProductData.getAllGroups(productId),
        });
        handleSyncAllTabPanels();
        handleSyncSingleDetailGroup();
      } else {
        CustomNotification.error(result?.message ?? 'Something went wrong!');
      }
    },
    onError: () => {
      CustomNotification.error('Something went wrong!');
    },
  });
};

export const useDeleteGroupProduct = (productId) => {
  const queryClient = useQueryClient();

  const { handleSyncAllTabPanels } = useInvalidateFactsPanel(productId);

  return useMutation({
    mutationFn: productServices.deleteGroupProductData,
    onSuccess: async (result) => {
      if (result?.isSuccess) {
        queryClient.invalidateQueries({
          queryKey: groupProductData.getAllGroups(productId),
        });
        CustomNotification.success('Delete group successfully!');
        handleSyncAllTabPanels();
      } else {
        CustomNotification.error(result?.message ?? 'Something went wrong!');
      }
    },
    onError: () => {
      CustomNotification.error('Something went wrong!');
    },
  });
};
